// leaseholderChangeMatch — pure dedup logic for the Leaseholder Change builder,
// kept free of `./api` (and its Vite-only `import.meta.env`) so it is unit-
// testable under node:test/tsx.
//
// AppFolio pre-creates an approved applicant as a "Future"-status tenant, so the
// SAME person surfaces in BOTH a candidate's `currentResidents`
// (leaseholdersFromTenancy includes Future) AND its `applicants`. That is the
// double-listing. These helpers collapse it: the person shows ONCE — kept under
// "On the lease today" (flagged `incoming`/flippable, Miles's default), dropped
// from the applicants that seed "Additional residents".

import type {
  LeaseholderChangeApplicant,
  LeaseholderChangeResident,
} from './leaseholderChangeClient';

/** Normalize a full name for matching: trimmed, lowercased, whitespace-collapsed. */
function normName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`.trim().toLowerCase().replace(/\s+/g, ' ');
}

function normEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

/**
 * Whether a current resident and an approved-group applicant are the SAME
 * person. Applicants carry no tenant id, so we can't key on it — match on the
 * normalized full name with a NON-CONFLICTING email: equal when present on both
 * sides (the normal case — AppFolio copies the applicant's email onto the Future
 * tenant), or absent on at least one side. Two present-but-different emails are
 * treated as different people (never merged). A blank name never matches.
 */
export function isSameLeaseholderPerson(
  resident: Pick<LeaseholderChangeResident, 'firstName' | 'lastName' | 'email'>,
  applicant: Pick<LeaseholderChangeApplicant, 'firstName' | 'lastName' | 'email'>,
): boolean {
  const rName = normName(resident.firstName, resident.lastName);
  const aName = normName(applicant.firstName, applicant.lastName);
  if (!rName || rName !== aName) return false;
  const rEmail = normEmail(resident.email);
  const aEmail = normEmail(applicant.email);
  if (rEmail && aEmail) return rEmail === aEmail;
  return true; // name matches, no conflicting email → same person
}

/** A current resident, plus whether they are ALSO an incoming approved
 *  applicant (the dual-listed Future tenant) — those get the "move to new
 *  tenants" flip control and default to "On the lease today". */
export type PartitionedResident = LeaseholderChangeResident & { incoming: boolean };

export interface PartitionedCandidatePeople {
  /** Current residents, each flagged `incoming` when they also appear on the
   *  incoming application. */
  residents: PartitionedResident[];
  /** Applicants with the dual-listed people REMOVED — they render exactly once
   *  (under "On the lease today" by default; flip moves them to new tenants). */
  applicants: LeaseholderChangeApplicant[];
}

/**
 * Dedup a candidate's `currentResidents` against its `applicants`: a person on
 * BOTH lists must show ONCE. They are kept under current residents — flagged
 * `incoming` (flippable) and defaulted to "On the lease today" — and removed
 * from the applicants list so "Additional residents" doesn't re-list them.
 * Matching is 1:1 (each applicant is consumed at most once).
 */
export function partitionCandidatePeople(
  currentResidents: LeaseholderChangeResident[],
  applicants: LeaseholderChangeApplicant[],
): PartitionedCandidatePeople {
  const matchedApplicantIds = new Set<string>();
  const residents: PartitionedResident[] = currentResidents.map((r) => {
    const match = applicants.find(
      (a) => !matchedApplicantIds.has(a.id) && isSameLeaseholderPerson(r, a),
    );
    if (match) matchedApplicantIds.add(match.id);
    return { ...r, incoming: !!match };
  });
  return {
    residents,
    applicants: applicants.filter((a) => !matchedApplicantIds.has(a.id)),
  };
}
